import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import TellyMagic from '../../images/telly-magic.png';
import SelectSmall from '../general-components/select-small';
import StylePack from './style-pack';
import { GreenroomState as GS } from '../../stores/types/types';
import { ContentTemplate } from '../../stores/reducers/generate-content';
import { setStylePackTypeFilter } from '../../stores/actions/user-interface';

export interface StylePacksTabProps {
	setStylePackInfo: (stylePackInfo: StylePackData, id: string) => void;
}

export interface StylePackGroup {
	type: 'segment' | 'title' | 'countdown' | 'walkup' | 'background';
	templates: ContentTemplate[];
}

export interface StylePackData {
	id: string;
	name: string;
	locked: boolean;
	stylePack: StylePackGroup[];
}

export enum StylePackContentType {
	SEGMENT = 'segment',
	TITLE = 'title',
	COUNTDOWN = 'countdown',
	WALKUP = 'walkup',
	BACKGROUND = 'background',
	TRANSITION = 'transition',
}

export enum StylePackItemTypes {
	Info = 'info',
	Carousel = 'carousel',
}

const contentFilterOptions = [
	{ label: 'All Content', value: undefined },
	{ label: 'Walkups', value: StylePackContentType.WALKUP },
	{ label: 'Segment cards', value: StylePackContentType.SEGMENT },
	{ label: 'Lowers', value: StylePackContentType.TITLE },
	{ label: 'Countdowns', value: StylePackContentType.COUNTDOWN },
	{ label: 'Transitions', value: StylePackContentType.TRANSITION },
];

export const templateLabels = {
	[StylePackContentType.SEGMENT]: 'Segment Card',
	[StylePackContentType.TITLE]: 'Lowers',
	[StylePackContentType.COUNTDOWN]: 'Countdown',
	[StylePackContentType.WALKUP]: 'Walkup',
	[StylePackContentType.BACKGROUND]: 'Background',
	[StylePackContentType.TRANSITION]: 'Transition',
};

const templateOrder = [
	StylePackContentType.WALKUP,
	StylePackContentType.TITLE,
	StylePackContentType.SEGMENT,
	StylePackContentType.COUNTDOWN,
	// StylePackContentType.BACKGROUND,
	// StylePackContentType.TRANSITION,
];

export const mediaTemplates = [
	StylePackContentType.SEGMENT,
	StylePackContentType.COUNTDOWN,
	StylePackContentType.WALKUP,
];

const miscellaneousStylePackTypes = [
	'brandlive-style-pack-20',
	'misc-walkup',
];

export interface StylePackType {
	stylePack: string;
	name: string;
}

export default function StylePacksTab(props: StylePacksTabProps) {
	const { setStylePackInfo } = props;

	const dispatch = useDispatch();

	const { contentTemplates, loadingTemplates } = useSelector((s: GS) => s.GeneratedContentReducer);
	const stylePackTypeFilter = useSelector((s: GS) => s.UserInterfaceReducer.stylePackTypeFilter);

	const [stylePacks, setStylePacks] = useState<StylePackData[]>([]);

	useEffect(() => {
		if (!loadingTemplates && contentTemplates.length > 0) {
			const types: StylePackType[] = [];
			contentTemplates.forEach((template: ContentTemplate) => {
				const index = types.findIndex(
					(type: StylePackType) => type.stylePack === template.stylePack,
				);
				if (index === -1 && !miscellaneousStylePackTypes.includes(template.stylePack)) {
					types.push({
						stylePack: template.stylePack,
						name: template.name,
					});
				}
			});

			types.push({
				stylePack: 'miscellaneous',
				name: 'Miscellaneous',
			});

			setStylePacks(types.map((sortedType: StylePackType) => {
				const { stylePack: type, name } = sortedType;
				const allTemplates = contentTemplates.filter((template: ContentTemplate) => {
					if (type !== 'miscellaneous') {
						return template.stylePack === type;
					}
					return miscellaneousStylePackTypes.includes(template.stylePack);
				});
				const locked = !allTemplates.find((template: ContentTemplate) => !template.locked);
				const stylePack = templateOrder.map((templateKey: string) => {
					const templates = allTemplates.filter(
						(template: ContentTemplate) => template.contentType === templateKey,
					);
					return {
						type: templateKey,
						templates,
					} as StylePackGroup;
				});

				return {
					id: type,
					name: type !== 'miscellaneous'
						? allTemplates?.[0]?.name
						: name,
					locked,
					stylePack,
				};
			}) as StylePackData[]);
		}
	}, [contentTemplates, loadingTemplates]);

	return (
		<div className="style-packs-tab">
			<div className="magic-telly-wand-gradient" data-testid="magic-telly-wand-gradient" />

			<div className="magic-telly-wand-small-gradient" data-testid="magic-telly-wand-small-gradient">
				<svg xmlns="http://www.w3.org/2000/svg" width="67" height="50" viewBox="0 0 67 50" fill="none">
					<g opacity="0.5" filter="url(#filter0_f_22823_9913)">
						<ellipse cx="13.5" cy="12.5" rx="13.5" ry="12.5" transform="matrix(-1 0 0 1 47 5)" fill="#FFF85C"/>
					</g>
					<defs>
						<filter id="filter0_f_22823_9913" x="0" y="-15" width="67" height="65" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix"/>
							<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
							<feGaussianBlur stdDeviation="10" result="effect1_foregroundBlur_22823_9913"/>
						</filter>
					</defs>
				</svg>
			</div>

			<div className="magic-tell-wand-circle" data-testid="magic-tell-wand-circle">
				<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
					<g filter="url(#filter0_f_22823_9912)">
						<circle cx="3.5" cy="3.5" r="3.5" transform="matrix(-1 0 0 1 11 4)" fill="white"/>
					</g>
					<defs>
						<filter id="filter0_f_22823_9912" x="0" y="0" width="15" height="15" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
							<feFlood floodOpacity="0" result="BackgroundImageFix"/>
							<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
							<feGaussianBlur stdDeviation="2" result="effect1_foregroundBlur_22823_9912"/>
						</filter>
					</defs>
				</svg>
			</div>

			<div className="magic-telly-icon" data-testid="magic-telly-icon">
				<img src={TellyMagic} height={65} width={65} />
			</div>

			<div className="style-packs-filters" data-testid="style-packs-filters">
				<div className="style-packs-filter-sort-options" data-testid="style-packs-filter-sort-options">
					<div className="style-packs-filter-content">
						<SelectSmall
							options={contentFilterOptions}
							onChange={(newFilter) => dispatch(setStylePackTypeFilter(newFilter))}
							label=""
							selected={contentFilterOptions.find(
								(o) => o.value === stylePackTypeFilter,
							) ?? contentFilterOptions[0]}
							hasCarrot
						/>
					</div>
				</div>
			</div>

			<div
				className={classNames('style-packs', { filtered: stylePackTypeFilter !== undefined })}
				data-testid="style-packs"
			>
				{stylePacks.map((group) => (
					<StylePack
						key={group.id}
						id={group.id}
						name={group.name}
						content={group.stylePack}
						setStylePackInfo={setStylePackInfo}
						locked={group.locked}
						filter={stylePackTypeFilter}
					/>
				))}
			</div>
		</div>
	);
}
